
import React, { useState } from "react";
import { Link } from "react-router-dom";
import cards from "../data/CardData-zh";
import {
  transferPartners,
  hotelPartners,
  airlinePartners,
  partnerDetails,
} from "../data/TransferPartners";

const PointsSearch = () => {
  const [selectedPartner, setSelectedPartner] = useState("");
  const [matchingCards, setMatchingCards] = useState([]);

  const handleSearch = (partner) => {
    setSelectedPartner(partner);
    if (!partner) {
      setMatchingCards([]);
      return;
    }

    const results = cards.filter((card) => {
      const partners = transferPartners[card.id] || [];
      return partners.includes(partner);
    });
    setMatchingCards(results);
  };

  return (
    <div className="container mx-auto px-6 py-8 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
        Hotel & Airline Points Search
      </h1>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        Select a hotel or airline loyalty program below to find which credit
        cards allow you to transfer points to that partner.
      </p>

      {/* Search Input Section */}
      <div className="flex justify-center mb-12">
        <div className="w-full max-w-md">
          <label
            htmlFor="partner-select"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Select Transfer Partner
          </label>
          <select
            id="partner-select"
            className="block w-full px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
            value={selectedPartner}
            onChange={(e) => handleSearch(e.target.value)}
          >
            <option value="">-- Choose a Partner --</option>
            <optgroup label="Hotels">
              {hotelPartners.map((partner) => (
                <option key={partner} value={partner}>
                  {partner}
                </option>
              ))}
            </optgroup>
            <optgroup label="Airlines">
              {airlinePartners.map((partner) => (
                <option key={partner} value={partner}>
                  {partner}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
      </div>

      {/* Results Section */}
      {selectedPartner && (
        <div className="space-y-6">
          {/* Partner Details Card */}
          {partnerDetails[selectedPartner] && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">
                    {selectedPartner}
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {partnerDetails[selectedPartner].description}
                  </p>
                </div>
                <a
                  href={partnerDetails[selectedPartner].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition duration-200"
                >
                  Visit Website &rarr;
                </a>
              </div>
            </div>
          )}

          <h2 className="text-2xl font-semibold text-gray-800 border-b pb-2 mb-6">
            Cards that transfer to{" "}
            <span className="text-blue-600">{selectedPartner}</span>
          </h2>

          {matchingCards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {matchingCards.map((card) => (
                <div
                  key={card.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100 flex flex-col"
                >
                  <div className="p-6 flex flex-col items-center flex-grow">
                    <div className="h-40 w-full flex items-center justify-center mb-4">
                        <img
                        src={card.image}
                        alt={card.name}
                        className="max-h-full max-w-full object-contain"
                        />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                      {card.name}
                    </h3>
                    <div className="text-sm text-gray-500 mb-4">
                      <div className="mb-1">
                        <span className="font-semibold">Annual Fee:</span> $
                        {card.annualFee}
                      </div>

                      {(() => {
                        let finalCostAmount = null;

                        if (card.costBreakdown) {
                          const finalCostItem = card.costBreakdown.find(
                            (item) => item.description === "Final Annual Cost"
                          );
                          if (finalCostItem) {
                            finalCostAmount = finalCostItem.amount;
                          }
                        }

                        if (finalCostAmount === null && card.annualFee === 0) {
                          finalCostAmount = 0;
                        }

                        if (finalCostAmount !== null) {
                          return (
                            <div>
                              <span className="font-semibold">Final Cost:</span>{" "}
                              <span
                                className={
                                  finalCostAmount > 0
                                    ? "text-red-500 font-bold"
                                    : "text-green-600 font-bold"
                                }
                              >
                                {finalCostAmount > 0
                                  ? `$${finalCostAmount}`
                                  : finalCostAmount === 0
                                  ? "$0"
                                  : `- $${Math.abs(finalCostAmount)}`}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                   
                    <div className="flex space-x-3 mt-auto w-full">
                       <Link
                        to={`/card/${card.id}`}
                        className="flex-1 bg-blue-600 text-white text-center py-2 rounded-lg font-semibold hover:bg-blue-700 transition duration-200"
                      >
                        View Details
                      </Link>
                      {card.applyLink && (
                          <a
                            href={card.applyLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-green-500 text-white text-center py-2 rounded-lg font-semibold hover:bg-green-600 transition duration-200"
                          >
                            Apply Now
                          </a>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500 text-lg">
                No cards found that transfer directly to {selectedPartner}.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PointsSearch;
